import {graphql} from 'react-relay';
import type {Environment, MutationParameters, PayloadError} from 'relay-runtime';
import Util from '../../../util/util';
import {trackAndCommitMutation} from '../../../../../tracking/amplitude/TrackMutation';
import {MODAL_TYPE, showModal} from '../generic_modal_conductor';

const mutation = graphql`
	mutation cancelPhaseSignoffRequestMutation($input: CancelPhaseSignoffRequestInput!) {
		cancelPhaseSignoffRequest(input: $input) {
			phase {
				id
				portalConfig {
					signoffRequestState
					signoffRequestDeadline
					signoffRequestApproverPortalUserId
					signoffChangesRequestedNote
				}
			}
		}
	}
`;

const CancelPhaseSignoffRequestMutation = {
	commit(
		environment: Environment,
		input: {psaMilestoneId: string},
		onSuccess: (response: unknown, errors: readonly PayloadError[] | null | undefined) => void,
		onError: (error: Error) => void
	) {
		return trackAndCommitMutation<MutationParameters>(environment, {
			mutation,
			variables: {
				input: {
					csrfToken: Util.getCsrfValue(),
					socketClientId: Util.getClientId(),
					...input,
				},
			},
			onCompleted: onSuccess,
			onError,
		} as never);
	},
};

/**
 * Withdraw the phase's open client sign-off request. `psaMilestoneId` is the phase's Relay
 * global id; the graph decodes it.
 *
 * Every selected field is one the withdrawal clears, so the Sign-off Status cell drops back
 * to "Ready to send" off the Relay merge alone — selecting only `signoffRequestState` would
 * leave a stale deadline and note in the store for the next request to inherit.
 *
 * Refusals are left to the shared warning modal rather than handled here: there is no inline
 * surface in a table cell, and both refusals (the request was decided, or already withdrawn)
 * mean the same thing — what is on screen is stale. `retry=false`, as for send: the write
 * notifies the client, and a retried withdrawal of an already-withdrawn request would arrive
 * as the very refusal that mapping exists to explain.
 */
export function cancelPhaseSignoffRequest(psaMilestoneId: string, onSuccess: () => void = () => undefined): void {
	// Defaulted, not optional: it is handed straight to Relay as `onCompleted`, and the
	// caller here has nothing to do on success — the cell re-renders off the store merge.
	Util.CommitMutation(CancelPhaseSignoffRequestMutation, {psaMilestoneId}, onSuccess, false);
}

/**
 * Ask first, then withdraw. The trigger is a link in a dense table row, and the client has
 * already been emailed — a stray click must not silently retract a request they are part-way
 * through answering.
 *
 * `intl` is passed in rather than hooked so this stays callable from a cell that already
 * holds one.
 */
export function confirmCancelPhaseSignoffRequest({
	phaseId,
	phaseName,
	intl,
}: {
	phaseId: string;
	phaseName: string;
	intl: {formatMessage: (descriptor: {id: string}, values?: Record<string, unknown>) => string};
}): void {
	showModal({
		type: MODAL_TYPE.CONFIRMATION,
		title: intl.formatMessage({id: 'client_portal.signoff_state.cancel_confirm_title'}),
		description: intl.formatMessage({id: 'client_portal.signoff_state.cancel_confirm_description'}, {name: phaseName}),
		callbackPositive: () => cancelPhaseSignoffRequest(phaseId),
	});
}
